import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAppKitAccount, useAppKitProvider, useDisconnect } from '@reown/appkit/react';
import { useNavigate } from 'react-router-dom';
import { metamaskLoginVerification } from '../../ApiService';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import './WalletInfo.css';

const WalletInfo = () => {
  const { address, isConnected } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider('eip155');
  const { disconnect } = useDisconnect();
  const navigate = useNavigate();
  const previousConnectedState = useRef(false);
  const processingDisconnect = useRef(false);
  const processingAuth = useRef(false);
  const processedAddresses = useRef(new Set()); // Track which addresses we've already processed
  const currentProcessingAddress = useRef(null); // Track currently processing address
  const [isAwaitingSignature, setIsAwaitingSignature] = useState(false);

  // Function to check if signature is expired (2 hours) from stored JSON at key "address"
  const isSignatureExpired = (walletAddress) => {
    if (!walletAddress) return true;
    try {
      const raw = localStorage.getItem(walletAddress);
      if (!raw) return true;
      const { timestamp } = JSON.parse(raw);
      const ts = Number(timestamp);
      const now = Date.now();
      const twoHoursInMs = 2 * 60 * 60 * 1000;
      return !Number.isFinite(ts) || now - ts > twoHoursInMs;
    } catch (_) {
      return true;
    }
  };

  // Function to safely disconnect and clear data
  const handleDisconnection = async () => {
    try {
      // Clear all local storage data first
      localStorage.removeItem("accountId");
      localStorage.removeItem("access_token");
      localStorage.removeItem("last_login");
      localStorage.removeItem("wallet_balance");
      if (address) {
        localStorage.removeItem(address);
      }

      // Try to disconnect if the function is available
      if (typeof disconnect === 'function') {
        try {
          await disconnect();
        } catch (error) {
          console.debug('Disconnect function failed silently:', error);
        }
      }

      // Always navigate to login, regardless of disconnect success
      navigate("/login");
    } catch (error) {
      console.debug('Disconnection process failed silently:', error);
      // Still navigate to login even if something fails
      navigate("/login");
    }
  };

  // Handle wallet disconnection and account changes
  useEffect(() => {
    // Handle disconnection
    if (previousConnectedState.current && !isConnected && !processingDisconnect.current) {
      processingDisconnect.current = true;
      handleDisconnection();
      setTimeout(() => {
        processingDisconnect.current = false;
      }, 1000);
    }
    // Handle account change
    else if (isConnected && address && !processingAuth.current) {
      // Only trigger authentication if the account has changed
      const lastAccount = localStorage.getItem("accountId");
      if (lastAccount && lastAccount.toLowerCase() !== address.toLowerCase()) {
        handleMetaMaskAuth();
      }
    }
    
    previousConnectedState.current = isConnected;
  }, [isConnected, address, navigate]);

  // Update wallet balance in local storage
  useEffect(() => {
    const updateWalletBalance = async () => {
      if (isAwaitingSignature) {
        return;
      }

      try {
        if (isConnected && address && window.ethereum && !isSignatureExpired(address)) {
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          const balanceWei = await provider.getBalance(address);
          const balanceEth = ethers.utils.formatEther(balanceWei);
          
          localStorage.setItem("wallet_balance", balanceEth);
          window.dispatchEvent(new CustomEvent('walletBalanceUpdated', { 
            detail: { balance: balanceEth }
          }));
        }
      } catch (error) {
        console.debug('Balance update failed silently:', error);
        handleDisconnection();
      }
    };
    
    if (isConnected && address && !isAwaitingSignature) {
      updateWalletBalance();
      const intervalId = setInterval(updateWalletBalance, 10000);
      return () => clearInterval(intervalId);
    }
  }, [isConnected, address, navigate, isAwaitingSignature]);

  // Handle MetaMask authentication (stateless, stores signature only locally)
  const handleMetaMaskAuth = useCallback(async () => {
    if (processingAuth.current || !isConnected || !address) return;
    
    // Global duplicate prevention - check if any signature request is already in progress
    if (window.simuleSignatureInProgress) {
      console.log("GLOBAL DUPLICATE PREVENTION: Signature already in progress globally");
      return;
    }
    
    window.simuleSignatureInProgress = true;
    
    try {
      processingAuth.current = true;
      setIsAwaitingSignature(true);
      
      // Clear any legacy token-based data
      localStorage.removeItem("access_token");
      sessionStorage.removeItem("access_token");
      
      const storedSignature = localStorage.getItem(address);
      
      // Check for valid non-expired signature first
      if (storedSignature && !isSignatureExpired(address)) {
        try {
          const parsed = JSON.parse(storedSignature);
          const signature = parsed.signature;
          const message = parsed.message;
          const date = new Date().toISOString();
          
          const authData = {
            message: message,
            signature: signature,
            account: address,
            date: date
          };
          
          const res = await metamaskLoginVerification(authData);
          const httpOk = res && res.status >= 200 && res.status < 300;
          const bodyNotExplicitFail = !(res?.data && (res.data.success === false || res.data.error));
          if (httpOk && bodyNotExplicitFail) {
            localStorage.setItem("accountId", address);
            localStorage.setItem("last_login", date);
            setIsAwaitingSignature(false);
            processingAuth.current = false;
            console.log("AUTH SUCCESS: Signature verified, redirecting to map", { address });
            navigate("/map");
            return true;
          }
        } catch (error) {
          console.debug('Existing signature verification failed, requesting new signature:', error);
          // Continue to request new signature if verification fails
        }
      }
      
      // Request new signature
      const date = new Date().toISOString();
      const timestamp = Date.now();
      const message = `Simule Authentication\nWallet: ${address}\nTimestamp: ${timestamp}\nI accept the Simule Terms of Service and I am at least 13 years old.`;
      
      try {
        const signature = await window.ethereum.request({
          method: 'personal_sign',
          params: [message, address]
        });
        
        // Store per requirements: key = wallet address, value = signature
        // And store metadata separately
        const authData = {
          message: message,
          signature: signature,
          account: address,
          date: date
        };
        
        const res = await metamaskLoginVerification(authData);
        const httpOk = res && res.status >= 200 && res.status < 300;
        const bodyNotExplicitFail = !(res?.data && (res.data.success === false || res.data.error));
        if (!httpOk || !bodyNotExplicitFail) {
          throw new Error('Invalid authentication response');
        }
        localStorage.setItem(address, JSON.stringify({ signature, message, timestamp }));
        localStorage.setItem("accountId", address);
        localStorage.setItem("last_login", date);
        
        setIsAwaitingSignature(false);
        console.log("AUTH SUCCESS: New signature verified, redirecting to map", { address });
        navigate("/map");
        return true;
        
      } catch (error) {
        console.error('Authentication error:', error);
        setIsAwaitingSignature(false);
        handleDisconnection();
        toast.error("Authentication failed. Please try again.");
        return false;
      } finally {
        processingAuth.current = false;
      }
    } catch (error) {
      console.error('Unexpected error in authentication:', error);
      setIsAwaitingSignature(false);
      processingAuth.current = false;
      return false;
    } finally {
      // Always clear the global flag
      window.simuleSignatureInProgress = false;
    }
  }, [address, disconnect, isConnected, navigate]);

  // Handle initial authentication and account changes
  useEffect(() => {
    const checkAndAuthenticate = async () => {
      if (processingAuth.current || !isConnected || !address || isAwaitingSignature) return;
      
      // Prevent duplicate processing of the same address
      if (processedAddresses.current.has(address)) {
        console.log("ADDRESS ALREADY PROCESSED: Skipping duplicate processing for", address);
        return;
      }
      
      // Prevent concurrent processing of the same address
      if (currentProcessingAddress.current === address) {
        console.log("ADDRESS ALREADY PROCESSING: Skipping concurrent processing for", address);
        return;
      }
      
      const storedSignature = localStorage.getItem(address);
      
      // If we have a valid signature, don't do anything
      if (storedSignature && !isSignatureExpired(address)) {
        console.log("VALID SIGNATURE: Found valid signature for address", address);
        processedAddresses.current.add(address);
        return;
      }
      
      // Otherwise, authenticate
      console.log("NO VALID SIGNATURE: Requesting new signature for address", address);
      processedAddresses.current.add(address);
      currentProcessingAddress.current = address;
      
      try {
        await handleMetaMaskAuth();
      } finally {
        currentProcessingAddress.current = null;
      }
    };
    
    // Only run once when address changes, not on interval
    checkAndAuthenticate();
  }, [isConnected, address]); // Removed handleMetaMaskAuth from deps to prevent circular dependency

  // Block all UI interaction while waiting for signature
  useEffect(() => {
    if (isAwaitingSignature) {
      const overlay = document.createElement('div');
      overlay.id = 'signature-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        font-size: 18px;
      `;
      overlay.innerHTML = '<div>Please complete the signature request to continue...</div>';
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
      
      return () => {
        if (document.getElementById('signature-overlay')) {
          document.body.removeChild(overlay);
          document.body.style.overflow = '';
        }
      };
    }
  }, [isAwaitingSignature]);

  return null;
};

export default WalletInfo;
